import { ActionContext } from "vuex/types";
import { SnackQueue as Queue } from "@/plugins/SnackQueue";
import {
  IGiveKnowledge,
  IGiveKnowledgeError,
  IGiveKnowledgeRequest
} from "@/store/types/user-types";
import { GIVE_KNOWLEDGE_ERROR } from "@/utils/enumerate";
import { RootState } from "@/store/types/root";

const state = {
  listGiveKnowledge: [] as IGiveKnowledge[]
};

type State = typeof state;

const giveKnowledgeIndexError = (result: any, index: number) => {
  const fail = result.list
    ? result.list[index]?.message?.[0]
    : result.data?.[0]?.message;
  const failIndex: IGiveKnowledgeError = fail
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
  return GIVE_KNOWLEDGE_ERROR[failIndex] || "";
};

const mutations = {
  setListGiveKnowledge: (state: State, users: IGiveKnowledge[]) => {
    users.sort((first, second) =>
      first.group_by.localeCompare(second.group_by)
    );
    state.listGiveKnowledge.push(...users);
  },

  clearListGiveKnowledge: (state: State) => {
    state.listGiveKnowledge = [];
  },

  checkListGiveKnowledge: (state: State) => {
    const hasPendingFile = state.listGiveKnowledge.some(
      user => user.status === "pending"
    );
    if (!hasPendingFile) {
      Queue.open({
        id: "give_knowledge",
        canClose: true
      });
    }
  }
};

const actions = {
  tryGiveKnowledge: (
    { commit, dispatch }: ActionContext<State, RootState>,
    item = "all"
  ) => {
    state.listGiveKnowledge.forEach((action: IGiveKnowledge) => {
      if (
        ["error", "abort"].includes(action.status) &&
        (item === "all" || action.index === item || action.group_by === item)
      ) {
        action.status = "pending";
        action.percentage = 0;
      }
    });
    dispatch("giveKnowledgeTrigger");
    commit("checkListGiveKnowledge");
  },

  sendGiveKnowledge: async (
    { commit, dispatch }: ActionContext<State, RootState>,
    group_by: string
  ) => {
    const list = state.listGiveKnowledge.filter(
      user => user.group_by === group_by && user.percentage === 0
    );
    if (list.length) {
      list.forEach(item => {
        item.percentage = 25;
        item.status = "pending";
      });
      const item = list[0];
      let giveKnowledgeData: IGiveKnowledgeRequest = {
        process_id: item.processId,
        user: {
          id: item.user.id,
          name: item.user.name,
          capacity: {
            entityId: item.user?.capacity_selected?.entityId,
            id: item.user?.capacity_selected?.id
          },
          email: item.user.email
        },
        LGPD: item.LGPD,
        observation: item.observation,
        is_external: item.isExternal
      };

      let ACTION = "postGiveKnowledgeProcess";

      if (!item.isProcess) {
        ACTION = "postGiveKnowledgeFile";
        const fileIds = list.map(item => item.process_file_id);

        giveKnowledgeData = {
          ...giveKnowledgeData,
          process_file_ids: fileIds
        };
      }

      const result = await dispatch(ACTION, giveKnowledgeData);
      list.forEach((item: IGiveKnowledge, index: number) => {
        item.status = result.state ? "done" : "error";
        if (!result.state) {
          item.error = giveKnowledgeIndexError(result, index);
        }
      });
    }
    dispatch("giveKnowledgeTrigger");
    commit("checkListGiveKnowledge");
  },

  giveKnowledgeTrigger: async ({
    state,
    dispatch
  }: ActionContext<State, RootState>) => {
    const hasGiveKnowledge = state.listGiveKnowledge.some(
      user => user.status === "pending" && user.percentage != 0
    );

    if (!hasGiveKnowledge) {
      const userToGiveKnowledge = state.listGiveKnowledge.find(
        user => user.status === "pending" && user.percentage === 0
      );
      if (userToGiveKnowledge)
        await dispatch("sendGiveKnowledge", userToGiveKnowledge.group_by);
    }
  }
};

const getters = {
  getListGiveKnowledge: (state: State) => state.listGiveKnowledge
};

export default {
  state,
  mutations,
  actions,
  getters
};
